import * as d3 from 'd3';
import styled from 'styled-components';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import type { FormatNodeValue, SizedNode } from '../../types';
import {
  measurerForSize,
  SVG_LABEL_LINE_HEIGHT,
  SVG_LABEL_SIZE,
  toBlockCenterOffset,
  toLineHeight,
  toTextColor,
  type GroupLabel,
} from '../svgLabels';
import { LEAF_LINE_COUNTS, toCircleLines } from './toCircleLines';

const Text = styled.text`
  text-anchor: middle;
  dominant-baseline: middle;
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  pointer-events: none;
`;

type HierarchicalPackingViewLabelProperties = {
  node: d3.HierarchyCircularNode<SizedNode>;
  formatValue: FormatNodeValue;
  /** The size and band the layout reserved for this group, or `null` for a leaf. */
  groupLabel: GroupLabel | null;
};

/**
 * What a circle says about itself: the value reserved first and the name truncated around it,
 * as the treemap's tiles are, falling back to the name alone where the number will not fit.
 *
 * A group is labelled along the top of its circle rather than through the middle, which is where
 * its children are. That is the one place a group can say what it is without colliding with
 * them, and until it had one the hierarchy was drawn with every ring unnamed.
 */
export const HierarchicalPackingViewLabel = ({
  node,
  formatValue,
  groupLabel,
}: HierarchicalPackingViewLabelProperties) => {
  const {
    x,
    y,
    r,
    value,
    data: { label, isDark },
  } = node;
  /* A circle too small to hold a glyph, which is the one case `toLeafText` answers with the
     untruncated label rather than measuring against it. Only a real radius reaches here — the
     view does not lay out at all when the tree sums to nothing, where `d3.pack` would give
     `NaN` and this comparison would be false. */
  if (2 * r < SVG_LABEL_SIZE) return null;
  /* The very size and band the layout reserved for this group, so the label and the room made
     for it cannot disagree. A leaf writes at the base size. */
  const group = groupLabel;
  const labelSize = group?.labelSize ?? SVG_LABEL_SIZE;
  const lineHeight = group ? toLineHeight(labelSize) : SVG_LABEL_LINE_HEIGHT;
  const measure = measurerForSize(labelSize);

  /* A group takes the top of its circle, which is the only place it can write without covering
     its children — and the chord is short up there. So it writes its name and not its value:
     the circle's area already is the value, and printing it too left the name at one letter
     (`O… (5.9M)`). A leaf has no children to avoid, so it takes the middle and says both. */
  const centerOffset = group
    ? toBlockCenterOffset({
        r,
        band: group.band,
        lineCount: Math.max(1, group.lines.length),
        lineHeight,
      })
    : 0;
  const lines = group
    ? group.lines
    : toCircleLines({
        label,
        valueLabel: `(${formatValue(value)})`,
        r,
        lineCounts: LEAF_LINE_COUNTS,
        centerOffset,
        measure,
        lineHeight,
      });
  if (!lines.length) return null;

  const firstLineOffset = ((lines.length - 1) * lineHeight) / 2;
  return (
    <Text
      x={x}
      y={y - centerOffset - firstLineOffset}
      style={{ fill: toTextColor(isDark), fontSize: labelSize }}
    >
      {lines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? 0 : lineHeight}>
          {line}
        </tspan>
      ))}
    </Text>
  );
};
